/**
 * Intent shortcuts for the library search (X6 / LIB-05).
 *
 * People arrive with a feeling ("something funny"), a car ("cybertruck") or a
 * category ("memes"), not a title. Each of those has a hub that answers it
 * better than a text match, so the library points there. Every target is a page
 * that already exists and is built from real data: the vibe pages
 * (lib/vibes.ts), the curated collections (lib/curated-collections.ts), the
 * model pages (lib/model-data.ts) and the category hubs (lib/category-data.ts).
 * Nothing here defines a model, category or collection of its own.
 */
import { categoryData } from './category-data'
import { curatedCollections } from './curated-collections'
import { MERGED_CATEGORY_REDIRECTS } from './merged-category-redirects'
import { modelData } from './model-data'
import { VIBES } from './vibes'

export interface IntentTarget {
  /** Short word for a chip, e.g. "Funny". */
  label: string
  href: string
}

/** The three moods the library offers without typing. */
export const INTENT_SHORTCUTS: readonly IntentTarget[] = [
  { label: 'Funny', href: '/sounds/vibe/comedy' },
  { label: 'Subtle', href: '/sounds/vibe/minimalist' },
  { label: 'Classic', href: '/sounds/collection/classic-tv-movies' },
]

/** Words that mean one of the three moods. */
const MOOD_WORDS: Record<string, IntentTarget> = {
  funny: INTENT_SHORTCUTS[0],
  funniest: INTENT_SHORTCUTS[0],
  humor: INTENT_SHORTCUTS[0],
  humour: INTENT_SHORTCUTS[0],
  hilarious: INTENT_SHORTCUTS[0],
  subtle: INTENT_SHORTCUTS[1],
  quiet: INTENT_SHORTCUTS[1],
  soft: INTENT_SHORTCUTS[1],
  gentle: INTENT_SHORTCUTS[1],
  minimal: INTENT_SHORTCUTS[1],
  classic: INTENT_SHORTCUTS[2],
  classics: INTENT_SHORTCUTS[2],
}

function normalize(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()
}

/** "tesla model 3 lock sounds" and "model 3" both name the car. */
function stripCarFraming(value: string): string {
  return value
    .replace(/\b(tesla|lock|chime|chimes|sound|sounds)\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function buildNameIndex(): Map<string, IntentTarget> {
  const index = new Map<string, IntentTarget>()
  const add = (key: string, target: IntentTarget) => {
    const normalized = normalize(key)
    if (normalized && !index.has(normalized)) index.set(normalized, target)
  }

  for (const model of Object.values(modelData)) {
    const target = { label: model.name, href: `/sounds/model/${model.slug}` }
    add(model.name, target)
    add(model.slug, target)
  }
  for (const vibe of VIBES) {
    add(vibe.name, { label: vibe.name, href: `/sounds/vibe/${vibe.slug}` })
  }
  for (const collection of curatedCollections) {
    add(collection.name, { label: collection.name, href: `/sounds/collection/${collection.id}` })
  }
  for (const category of Object.values(categoryData)) {
    // `trending` is an alias that reads as a mood, not a topic. A merged or
    // retired shelf (a key of MERGED_CATEGORY_REDIRECTS) 308s to its parent, so an intent must not point at it.
    if (category.slug === 'trending') continue
    if (Object.hasOwn(MERGED_CATEGORY_REDIRECTS, category.slug)) continue
    const target = { label: category.name, href: `/sounds/category/${category.slug}` }
    add(category.name, target)
    add(category.slug, target)
  }
  return index
}

let nameIndex: Map<string, IntentTarget> | undefined

/**
 * The hub a query is really asking for, or null. Matches the whole query, not
 * a word inside it: "mario death" is a title search, "memes" is a category.
 */
export function intentForQuery(query: string): IntentTarget | null {
  const normalized = normalize(query)
  if (!normalized) return null

  const mood = MOOD_WORDS[stripCarFraming(normalized)]
  if (mood) return mood

  nameIndex ??= buildNameIndex()
  return nameIndex.get(normalized) ?? nameIndex.get(stripCarFraming(normalized)) ?? null
}
